import React, { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outlined' | 'outline' | 'text' | 'ghost' | 'danger' | 'success' | 'warning' | 'info' | 'filled';
  color?: 'primary' | 'secondary' | 'danger' | 'red' | 'gray'; // Support legacy color prop with more options
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  isLoading?: boolean;
  loading?: boolean; // Alias for isLoading
  loadingText?: string;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>((
  {
    children,
    variant = 'primary',
    color,
    size = 'md',
    fullWidth = false,
    leftIcon,
    rightIcon,
    isLoading = false,
    loading = false,
    loadingText,
    disabled,
    className = '',
    ...props
  },
  ref
) => {
  // Use either isLoading or loading prop
  const isButtonLoading = isLoading || loading;
  // Base styles
  let baseStyle = 'inline-flex items-center justify-center rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors';
  
  // Get the appropriate color based on color prop
  const getColorStyle = (color?: string, variant?: string) => {
    // If color prop is used, apply these styles
    if (color) {
      // Normalize variant - treat 'filled' as 'primary'
      const normalizedVariant = variant === 'filled' ? 'primary' : variant;
      
      switch (color) {
        case 'primary':
          return normalizedVariant === 'outlined' || normalizedVariant === 'outline' 
            ? 'border-red-600 text-red-600 hover:bg-red-50 focus:ring-red-500' 
            : 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500';
        case 'secondary':
          return normalizedVariant === 'outlined' || normalizedVariant === 'outline' 
            ? 'border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500' 
            : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500';
        case 'danger':
          return normalizedVariant === 'outlined' || normalizedVariant === 'outline'
            ? 'border-error text-error hover:bg-red-50 focus:ring-error' 
            : 'bg-error text-white hover:bg-error-800 focus:ring-error';
        case 'red':
          return normalizedVariant === 'outlined' || normalizedVariant === 'outline'
            ? 'border-red-600 text-red-600 hover:bg-red-50 focus:ring-red-500' 
            : 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500';
        case 'gray':
          return normalizedVariant === 'outlined' || normalizedVariant === 'outline'
            ? 'border-gray-500 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-gray-400' 
            : 'bg-gray-500 text-white hover:bg-gray-600 focus:ring-gray-400';
        default:
          return '';
      }
    }
    return '';
  };
  
  // Variant styles
  const variantStyles = {
    primary: 'bg-red text-white hover:bg-red-800 focus:ring-red',
    secondary: 'bg-blue text-white hover:bg-blue-800 focus:ring-blue',
    filled: 'bg-red text-white hover:bg-red-800 focus:ring-red', // Alias for primary
    outlined: 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-blue',
    outline: 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-blue',
    text: 'text-blue-600 dark:text-blue-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-blue',
    ghost: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-gray-400',
    danger: 'bg-error text-white hover:bg-error-800 focus:ring-error',
    success: 'bg-success text-success-text hover:opacity-90 focus:ring-success',
    warning: 'bg-warning text-warning-text hover:opacity-90 focus:ring-warning',
    info: 'bg-info text-info-text hover:opacity-90 focus:ring-info'
  };
  
  // Size styles - Updated with minimum 44px height for touch targets
  const sizeStyles = {
    sm: 'px-3 py-2.5 text-sm min-h-[44px]',
    md: 'px-4 py-3 text-base min-h-[44px]',
    lg: 'px-6 py-3.5 text-lg min-h-[48px]'
  };
  
  // Width style
  const widthStyle = fullWidth ? 'w-full' : '';
  
  // Disabled style
  const disabledStyle = disabled || isButtonLoading ? 'opacity-60 cursor-not-allowed' : '';
  
  // Determine which style to use: either variant style or color style
  const styleToUse = color ? getColorStyle(color, variant) : variantStyles[variant];
  
  // Combine styles
  const combinedClassName = `${baseStyle} ${styleToUse} ${sizeStyles[size]} ${widthStyle} ${disabledStyle} ${className}`;
  
  return (
    <button
      ref={ref}
      className={combinedClassName}
      disabled={disabled || isButtonLoading}
      {...props}
    >
      {isButtonLoading ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {loadingText || children}
        </>
      ) : (
        <>
          {leftIcon && <span className="mr-2">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="ml-2">{rightIcon}</span>}
        </>
      )}
    </button>
  );
});

export default Button;